import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { parseApiError } from "@/lib/ai-error";
import { useWorkspace } from "@/hooks/use-workspace";
import { cn } from "@/lib/utils";
import { Loader2, Calendar, CheckCircle, Info, RefreshCw, CalendarOff, Clock } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { OfficeHoursPanel } from "@/components/admin/office-hours-panel";

// Task #708 — workspace-level calendar push field toggles. Order here
// drives render order in the dialog so admins see title fields together,
// then description fields, then the post-review email gate.
const CAL_PUSH_FIELDS: { key: string; label: string; help: string }[] = [
  { key: "calPushTitleAccountName", label: "Include account name in title", help: "Adds the client/account name to the calendar event title." },
  { key: "calPushTitleAddress", label: "Include address in title", help: "Adds the venue address to the calendar event title." },
  { key: "calPushTitleRebookPrefix", label: "Include 'Rebook' prefix in title", help: "Prefixes 'Rebook' to titles when the booking is a rebook." },
  { key: "calPushAddressInBody", label: "Include address in description", help: "Adds the venue address as a separate line in the event description." },
  { key: "calPushContactName", label: "Include contact name", help: "Adds the on-site contact name to the description." },
  { key: "calPushContactPhone", label: "Include contact phone", help: "Adds the on-site contact phone number to the description." },
  { key: "calPushRate", label: "Include event rate", help: "Adds the (net) event rate. Still respects per-talent financial visibility." },
  { key: "calPushReviewLink", label: "Include post-event review link", help: "Adds the artist's post-event review URL to the description." },
  { key: "calPushHoldStatus", label: "Show 'HOLD:' status prefix", help: "Prefixes the title and appends a Pending line when the booking is on hold." },
  { key: "allowPostReviewClientEmail", label: "Allow post-review client email", help: "Lets artists email the event contact from the public review page after submitting." },
];

// Task #405 — admin-only Google Meet transcription policy. Hybrid model:
// internal meetings can auto-transcribe; client meetings are opt-in per call.
const MEET_POLICY_FIELDS: { key: string; label: string; help: string; default: boolean }[] = [
  { key: "meetTranscriptionEnabled", label: "Enable Meet transcripts & AI notes", help: "Master switch. When off, no meetings are transcribed and the Meet transcription controls are hidden.", default: false },
  { key: "meetTranscribeInternalAuto", label: "Auto-transcribe internal meetings", help: "Internal meetings (team-only) default to transcribing automatically when a Meet link is added.", default: true },
  { key: "meetTranscribeClientAllowed", label: "Allow transcribing client meetings", help: "Lets users opt a client meeting into transcription. Client meetings are never auto-transcribed.", default: true },
];

type CalSettings = {
  additionalCalendarIds: string[];
} & Record<string, boolean>;

interface CalendarSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

interface CalendarInfo {
  id: string;
  summary: string;
  primary?: boolean;
}

export function CalendarSettingsDialog({ open, onOpenChange, inPanel = false }: CalendarSettingsDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Task #415 — workspace-scoped paths. The server fails closed with
  // WORKSPACE_REQUIRED on bare /api/calendar/... calls (no /w/:slug prefix),
  // so route every calendar call through buildPath like the rest of the app.
  const { buildPath } = useWorkspace();
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([]);
  // Task #708 — local state for the workspace push field toggles. Default
  // every key to true so the UI is never in an undefined-checked state
  // while the GET is in flight.
  const [pushFlags, setPushFlags] = useState<Record<string, boolean>>(() => ({
    ...Object.fromEntries(CAL_PUSH_FIELDS.map(f => [f.key, true])),
    ...Object.fromEntries(MEET_POLICY_FIELDS.map(f => [f.key, f.default])),
    // Task #410 — workspace-calendar Hub visibility (default on).
    showWorkspaceCalendarInHub: true,
  }));

  const { data: calData, isLoading: loadingCalendars, error: calendarsError } = useQuery<{ calendars: CalendarInfo[]; connected?: boolean }>({
    queryKey: [buildPath("/api/calendar/list")],
    enabled: open,
  });
  const availableCalendars = calData?.calendars || [];
  // The server returns { connected: false, calendars: [] } when no Google
  // Calendar is connected for this workspace — that's not an error, just an
  // empty state. Anything else that throws is a genuine load failure.
  const noCalendarConnected = calData?.connected === false;

  const { data: currentSettings, isLoading: loadingSettings } = useQuery<CalSettings>({
    queryKey: [buildPath("/api/calendar/settings")],
    enabled: open,
  });

  useEffect(() => {
    if (!currentSettings) return;
    if (currentSettings.additionalCalendarIds) {
      setSelectedCalendars(currentSettings.additionalCalendarIds);
    }
    setPushFlags(prev => {
      const next = { ...prev };
      for (const f of [...CAL_PUSH_FIELDS, ...MEET_POLICY_FIELDS]) {
        if (typeof currentSettings[f.key] === "boolean") next[f.key] = currentSettings[f.key];
      }
      // Task #410 — workspace-calendar Hub visibility toggle.
      if (typeof currentSettings.showWorkspaceCalendarInHub === "boolean") {
        next.showWorkspaceCalendarInHub = currentSettings.showWorkspaceCalendarInHub;
      }
      return next;
    });
  }, [currentSettings]);

  const updateSettingsMutation = useMutation({
    mutationFn: async (payload: { calendarIds: string[]; flags: Record<string, boolean> }) => {
      const response = await apiRequest("PUT", buildPath("/api/calendar/settings"), {
        additionalCalendarIds: payload.calendarIds,
        ...payload.flags,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/calendar/settings")] });
      toast({
        title: "Settings Saved",
        description: "Calendar availability settings have been updated.",
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      // Task #415 — surface a clean human message, never the raw
      // `400: {"message":...}` envelope that apiRequest throws.
      const { message } = parseApiError(error);
      toast({
        title: "Error",
        description: message || "Failed to save calendar settings.",
        variant: "destructive",
      });
    },
  });

  const forceRefreshMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", buildPath("/api/calendar/force-refresh"));
      return response.json();
    },
    onSuccess: (data: any) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: [buildPath("/api/calendar/list")] });
        queryClient.invalidateQueries({ queryKey: [buildPath("/api/calendar/settings")] });
        toast({
          title: "Token Refreshed",
          description: data.newExpiry ? `New expiry: ${new Date(data.newExpiry).toLocaleString()}` : "Calendar token has been refreshed.",
        });
      } else {
        toast({
          title: "Refresh Failed",
          description: data.message || "Failed to refresh calendar token. You may need to reconnect your calendar.",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      // Task #415 — surface a clean human message, never the raw
      // `400: {"message":...}` envelope that apiRequest throws.
      const { message } = parseApiError(error);
      toast({
        title: "Error",
        description: message || "Failed to refresh calendar token.",
        variant: "destructive",
      });
    },
  });

  const handleToggleCalendar = (calendarId: string) => {
    setSelectedCalendars(prev => 
      prev.includes(calendarId)
        ? prev.filter(id => id !== calendarId)
        : [...prev, calendarId]
    );
  };

  const handleSave = () => {
    updateSettingsMutation.mutate({ calendarIds: selectedCalendars, flags: pushFlags });
  };

  const togglePushFlag = (key: string, value: boolean) => {
    setPushFlags(prev => ({ ...prev, [key]: value }));
  };

  const isLoading = loadingCalendars || loadingSettings;

  const innerContent = (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Calendar Availability Settings
        </DialogTitle>
        <DialogDescription>
          Select which calendars to check when showing available time slots on the public booking page.
        </DialogDescription>
      </DialogHeader>

      {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : noCalendarConnected ? (
          <div className="space-y-4">
            <Alert data-testid="alert-no-calendar-connected">
              <CalendarOff className="h-4 w-4" />
              <AlertDescription>
                No Google Calendar is connected for this workspace yet. Connect one
                under Admin Panel → Integrations to start checking availability and
                pushing booked events to your calendar.
              </AlertDescription>
            </Alert>
          </div>
        ) : calendarsError ? (
          <div className="space-y-4">
            <Alert variant="destructive">
              <Info className="h-4 w-4" />
              <AlertDescription>
                Could not load calendars. The calendar token may have expired.
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-2">
              <Button 
                variant="outline" 
                onClick={() => forceRefreshMutation.mutate()}
                disabled={forceRefreshMutation.isPending}
                data-testid="button-force-refresh-calendar"
              >
                {forceRefreshMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh Calendar Token
              </Button>
              <p className="text-xs text-muted-foreground">
                If refresh fails, you may need to reconnect Google Calendar in Settings.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                The primary calendar is always checked. Select additional calendars below to also check for conflicts.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <div className="flex-1">
                  <p className="font-medium text-sm">
                    {availableCalendars.find(c => c.primary)?.summary || "Primary Calendar"}
                  </p>
                  <p className="text-xs text-muted-foreground">Always checked (default)</p>
                </div>
                <Badge variant="secondary">Primary</Badge>
              </div>

              {availableCalendars
                .filter(cal => !cal.primary)
                .map(calendar => (
                  <div
                    key={calendar.id}
                    className="flex items-center gap-3 p-3 rounded-lg border hover-elevate cursor-pointer"
                    onClick={() => handleToggleCalendar(calendar.id)}
                  >
                    <Checkbox
                      id={calendar.id}
                      checked={selectedCalendars.includes(calendar.id)}
                      onCheckedChange={() => handleToggleCalendar(calendar.id)}
                    />
                    <Label htmlFor={calendar.id} className="flex-1 cursor-pointer">
                      <p className="font-medium text-sm">{calendar.summary}</p>
                      <p className="text-xs text-muted-foreground truncate">{calendar.id}</p>
                    </Label>
                  </div>
                ))}

              {availableCalendars.filter(c => !c.primary).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No additional calendars found. Shared calendars will appear here.
                </p>
              )}
            </div>

            <Separator />

            {/* Task #410 — workspace calendar visibility in the Calendar Hub */}
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold">Calendar Hub</h4>
                <p className="text-xs text-muted-foreground">
                  Control what extra calendar sources team members can view in the Calendar Hub.
                </p>
              </div>
              <div className="flex items-start justify-between gap-3 p-3 rounded-lg border">
                <div className="flex-1 min-w-0">
                  <Label htmlFor="flag-showWorkspaceCalendarInHub" className="text-sm font-medium cursor-pointer">
                    Show workspace calendar in Calendar Hub
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Adds the shared workspace (fallback) Google Calendar as a view-only source in the Hub's
                    calendar dropdown, alongside "My Calendar" and teammates. Only appears when a workspace-level
                    calendar is connected.
                  </p>
                </div>
                <Switch
                  id="flag-showWorkspaceCalendarInHub"
                  checked={!!pushFlags.showWorkspaceCalendarInHub}
                  onCheckedChange={v => togglePushFlag("showWorkspaceCalendarInHub", !!v)}
                  data-testid="switch-showWorkspaceCalendarInHub"
                />
              </div>
            </div>

            <Separator />

            {/* Task #405 — Meet transcription policy (admin) */}
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold">Meeting Transcripts & AI Notes</h4>
                <p className="text-xs text-muted-foreground">
                  Google Meet meetings can be transcribed and summarized into AI notes.
                  Internal meetings auto-transcribe; client meetings are opt-in per call.
                  Each user grants Meet access once when they first enable it.
                </p>
              </div>
              <div className="space-y-3">
                {MEET_POLICY_FIELDS.map(field => {
                  const masterOff = !pushFlags["meetTranscriptionEnabled"];
                  const isMaster = field.key === "meetTranscriptionEnabled";
                  const disabled = !isMaster && masterOff;
                  return (
                    <div
                      key={field.key}
                      className={cn(
                        "flex items-start justify-between gap-3 p-3 rounded-lg border",
                        disabled && "opacity-50",
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <Label htmlFor={`flag-${field.key}`} className="text-sm font-medium cursor-pointer">
                          {field.label}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">{field.help}</p>
                      </div>
                      <Switch
                        id={`flag-${field.key}`}
                        checked={!!pushFlags[field.key]}
                        disabled={disabled}
                        onCheckedChange={v => togglePushFlag(field.key, !!v)}
                        data-testid={`switch-${field.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <Separator />

            {/* Task #708 — Calendar event content toggles */}
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold">Calendar Event Content</h4>
                <p className="text-xs text-muted-foreground">
                  Choose which fields are pushed to talents' calendar events. Per-talent
                  overrides can be configured in Manage Talent.
                </p>
              </div>
              <div className="space-y-3">
                {CAL_PUSH_FIELDS.map(field => (
                  <div
                    key={field.key}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg border"
                  >
                    <div className="flex-1 min-w-0">
                      <Label htmlFor={`flag-${field.key}`} className="text-sm font-medium cursor-pointer">
                        {field.label}
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">{field.help}</p>
                    </div>
                    <Switch
                      id={`flag-${field.key}`}
                      checked={!!pushFlags[field.key]}
                      onCheckedChange={v => togglePushFlag(field.key, !!v)}
                      data-testid={`switch-${field.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              {!inPanel && (
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
              )}
              <Button 
                onClick={handleSave}
                disabled={updateSettingsMutation.isPending}
                data-testid="button-save-calendar-settings"
              >
                {updateSettingsMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Settings
              </Button>
            </div>
          </div>
        )}

      {/* Task #416 — Office Hours now lives in Calendar Settings (the old
          Admin Panel → General section was removed). Rendered outside the
          calendar-availability conditional so it always shows, regardless of
          whether a Google Calendar is connected. */}
      <Separator className="my-2" />
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Office Hours
          </h4>
          <p className="text-xs text-muted-foreground">
            The display timezone for this workspace and the optional outbound
            office-hours envelope.
          </p>
        </div>
        <OfficeHoursPanel />
      </div>
    </>
  );

  if (inPanel) {
    return (
      <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
        {innerContent}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        {innerContent}
      </DialogContent>
    </Dialog>
  );
}
